import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toPng } from 'html-to-image';
import { firstValueFrom } from 'rxjs';
import { EnergyType, ViewType, VolumeEnum, API_BASE_URL } from '../models';
import { FILE_DELIVERY_SERVICE } from './file-delivery.service';

export interface ExportPdfCommand {
  titleLine1: string;
  titleLine2: string;
  startDate: Date;
  endDate: Date;
  energyType: EnergyType;
  viewType: ViewType;
  showAverage: boolean;
  showImbalanceDisclaimer: boolean;
  disclaimerText: string;
  tableDateFormat: string;
  datasetLabel: string;
  snapshotLabel: string;
  /** Widget language ("nl" / "fr" / "en"). The API renders month names with it —
   *  anonymous iframe visitors have no user settings for the backend to fall back on. */
  lang: string;
}

export interface ExportShareImageCommand {
  startDate: Date;
  endDate: Date;
  energyType: EnergyType;
  viewType: ViewType;
  volume: VolumeEnum;
  tableDateFormat: string;
  titleLine1: string;
  titleLine2: string;
  datasetLabel: string;
  timestampLabel: string;
  showAverage: boolean;
  averageFormatted: string;
  showImbalanceDisclaimer: boolean;
  disclaimerText: string;
  includeTableData: boolean;
  /** See ExportPdfCommand.lang. */
  lang: string;
}

@Injectable({ providedIn: 'root' })
export class ShareService {
  private readonly fileDelivery = inject(FILE_DELIVERY_SERVICE);
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL, { optional: true }) ?? '';

  buildPdfDownloadUrl(params: {
    startDate: Date;
    endDate: Date;
    energyType: EnergyType;
    viewType: ViewType;
    showAverage: boolean;
    lang: string;
  }): string {
    const p = new URLSearchParams({
      startDate: params.startDate.toISOString(),
      endDate: params.endDate.toISOString(),
      energyType: String(params.energyType),
      viewType: String(params.viewType),
      showAverage: String(params.showAverage),
      lang: params.lang,
    });
    return `${this.baseUrl}/api/EnergyPrices/export-pdf?${p.toString()}`;
  }

  async captureElement(el: HTMLElement): Promise<string> {
    return toPng(el, { cacheBust: true, pixelRatio: 2 });
  }

  async exportPdfViaApi(command: ExportPdfCommand): Promise<void> {
    const response = await firstValueFrom(
      this.http.post(`${this.baseUrl}/api/EnergyPrices/export-pdf`, command, { responseType: 'blob', observe: 'response' })
    );

    const blob = response.body!;
    const fileName = this.extractFileName(response.headers.get('content-disposition'), 'marktprijzen.pdf');
    await this.fileDelivery.shareBlob(blob, fileName, fileName);
  }

  private extractFileName(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) return fallback;
    const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i);
    return match?.[1]?.trim() ?? fallback;
  }

  async shareImageViaApi(command: ExportShareImageCommand): Promise<void> {
    const blob = await firstValueFrom(
      this.http.post(`${this.baseUrl}/api/EnergyPrices/export-share-image`, command, { responseType: 'blob' })
    );
    const dataUrl = await this.blobToDataUrl(blob);
    await this.shareViaSystem(dataUrl, 'Marktprijzen');
  }

  /**
   * Web-only counterpart of shareImageViaApi: saves the PNG as a file instead of
   * handing it to the OS share sheet. Goes through downloadFile rather than
   * shareImage so a browser exposing navigator.share cannot hijack it.
   */
  async downloadImageViaApi(command: ExportShareImageCommand, fileName: string): Promise<void> {
    const blob = await firstValueFrom(
      this.http.post(`${this.baseUrl}/api/EnergyPrices/export-share-image`, command, { responseType: 'blob' })
    );

    const objectUrl = URL.createObjectURL(blob);
    try {
      await this.fileDelivery.downloadFile(objectUrl, fileName);
    } finally {
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    }
  }

  async shareViaSystem(dataUrl: string, title: string): Promise<void> {
    await this.fileDelivery.shareImage(dataUrl, title);
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
